import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Send, Zap, MessageSquare, FileText, ScrollText } from 'lucide-react';
import { useApi } from '../utils/useApi';
import AutomaticNotificationsSettings from '../components/notifications/AutomaticNotificationsSettings';
import ManualNotificationsDashboard from '../components/notifications/ManualNotificationsDashboard';
import UnifiedNotificationLogs from '../components/notifications/UnifiedNotificationLogs';

interface Campaign {
  id: string;
  campaignUuid: string;
  tenantId: string;
  label: string;
  templateId: string;
  status: string;
  totalRecipients: number;
  excludedOptOut: number;
  sentCount: number;
  failedCount: number;
  createdAt: string;
  stats?: Record<string, number>;
  template: {
    id: string;
    title: string;
    templateName: string;
  } | null;
}

// Enhanced campaigns carry per-status counts in `stats`; legacy rows carry
// sentCount. Delivered/read messages were sent too.
const sentMessages = (campaign: Campaign): number =>
  campaign.stats
    ? (campaign.stats.sent || 0) + (campaign.stats.delivered || 0) + (campaign.stats.read || 0)
    : campaign.sentCount || 0;

type TabType = 'automatic' | 'manual' | 'logs';

export default function NotificationsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('automatic');

  const { fetchWithAuth } = useApi();

  // Get current user's gym ID (tenant ID)
  const { data: userData } = useQuery({
    queryKey: ['user-context'],
    queryFn: async () => {
      return await fetchWithAuth('/api/auth/context');
    }
  });

  const tenantId = userData?.data?.accessibleGyms?.[0];

  // Fetch campaigns for overview
  const { data: campaignsData } = useQuery({
    queryKey: ['enhanced-campaigns', tenantId],
    queryFn: async () => {
      if (!tenantId) return { data: [] };
      return await fetchWithAuth(`/api/enhanced-campaigns?gymId=${tenantId}&limit=5`);
    },
    enabled: !!tenantId
  });

  const campaigns: Campaign[] = campaignsData?.data || [];

  const tabs = [
    {
      id: 'automatic' as TabType,
      name: 'Automatic Notifications',
      icon: Zap,
      description: 'Manage automatic event-triggered notifications',
      disabled: false
    },
    {
      id: 'manual' as TabType,
      name: 'Manual Notifications & Campaigns',
      icon: Send,
      description: 'Create campaigns, manage scheduled messages, and view logs',
      disabled: false
    },
    {
      id: 'logs' as TabType,
      name: 'Notification Logs',
      icon: ScrollText,
      description: 'Every message sent for this gym — automatic and campaign',
      disabled: false
    }
  ];

  const renderTabContent = () => {
    if (!tenantId) {
      return (
        <div className="text-center py-12">
          <div className="text-gray-500 mb-4">No gym access found</div>
          <p className="text-sm text-gray-400">Please ensure you have access to a gym to use notifications</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'automatic':
        return <AutomaticNotificationsSettings gymId={tenantId} />;
      case 'manual':
        return <ManualNotificationsDashboard gymId={tenantId} />;
      case 'logs':
        return <UnifiedNotificationLogs gymId={tenantId} />;
      default:
        return <AutomaticNotificationsSettings gymId={tenantId} />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Premium Header */}
      <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-50/50 rounded-full blur-3xl -mr-32 -mt-32 transition-colors group-hover:bg-blue-100/50"></div>
        <div className="relative">
          <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none uppercase">Notifications Hub</h1>
          <p className="text-[10px] font-black text-gray-400 mt-2 uppercase tracking-[0.2em]">WhatsApp & System Messaging</p>
        </div>
      </div>

      {/* Quick Stats */}
      {campaigns.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <MessageSquare className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Campaigns</p>
                <p className="text-2xl font-semibold text-gray-900">{campaigns.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Send className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Messages Sent</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {campaigns.reduce((sum, campaign) => sum + sentMessages(campaign), 0)}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg border border-gray-200">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <FileText className="h-8 w-8 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Active Campaigns</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {campaigns.filter(c => ['SENDING', 'READY', 'SCHEDULED'].includes((c.status || '').toUpperCase())).length}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => !tab.disabled && setActiveTab(tab.id)}
                disabled={tab.disabled}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : tab.disabled
                    ? 'border-transparent text-gray-400 cursor-not-allowed opacity-60'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="mt-6">
        {renderTabContent()}
      </div>
    </div>
  );
}